import { Box, Flex, Grid, Heading, SimpleGrid, Stack, Text } from '@chakra-ui/react';

import { tracking } from '#features/analytics';
import { GridGlowBackground } from '#features/landing/components/Landing/GridGlowBackground';
import { Button } from '#features/ui';
import {
  COLLAB_TRACKS,
  CONTACT_EMAIL,
  DISCORD_URL,
  PROCESS_STEPS,
  VALUE_POINTS,
  type CollabTrack,
} from '../../data';

const ACCENT = '#4B50E6';

// Subtly elevated dark band — matches the careers/landing SectionBand so this page
// reads as part of the same product.
const BAND_ELEVATED =
  'linear-gradient(180deg, hsla(228, 11.6%, 11.5%, 1) 0%, hsla(228, 11.6%, 9.5%, 1) 100%)';

const MAILTO = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Partnership with Teamplay')}`;

const track = (action: string) => tracking.event({ action, category: 'partners_click' });

/** Full-bleed background band: background stretches edge-to-edge, content stays in the grid. */
const SectionBand = ({
  background,
  children,
}: {
  background: string;
  children: React.ReactNode;
}) => (
  <Box
    position="relative"
    w="100vw"
    ml="calc(50% - 50vw)"
    mr="calc(50% - 50vw)"
    background={background}
  >
    <Box
      position="absolute"
      inset={0}
      pointerEvents="none"
      background={`radial-gradient(ellipse 80% 60% at 50% 0%, ${ACCENT}14 0%, transparent 70%)`}
    />
    <Box
      position="relative"
      maxW={{ base: '640px', lg: '1200px' }}
      mx="auto"
      px={{ base: 4, md: 6, lg: 8 }}
    >
      {children}
    </Box>
  </Box>
);

const SectionHeader = ({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow: string;
  title: string;
  subtitle?: string;
}) => (
  <Box textAlign="center" mb={{ base: 8, lg: 10 }}>
    <Text
      textStyle="badge"
      color="purple.300"
      textTransform="uppercase"
      letterSpacing="0.15em"
      mb={3}
    >
      {eyebrow}
    </Text>
    <Heading
      as="h2"
      fontWeight="900"
      lineHeight="1.05"
      fontSize={{ base: '3xl', lg: '5xl' }}
      textTransform="uppercase"
      letterSpacing="-0.02em"
    >
      {title}
    </Heading>
    {subtitle && (
      <Text mt={4} textStyle={{ base: 'sm', md: 'md' }} color="fg.muted" maxW="620px" mx="auto">
        {subtitle}
      </Text>
    )}
  </Box>
);

const ExternalButton = ({
  href,
  action,
  children,
  ...rest
}: {
  href: string;
  action: string;
  children: React.ReactNode;
} & React.ComponentProps<typeof Button>) => (
  <Button asChild onClick={() => track(action)} {...rest}>
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  </Button>
);

const Hero = () => (
  <Stack
    gap={{ base: 6, lg: 7 }}
    alignItems={{ base: 'center', lg: 'flex-start' }}
    textAlign={{ base: 'center', lg: 'left' }}
    maxW="900px"
  >
    <Flex
      display="inline-flex"
      alignItems="center"
      gap={2}
      px={3}
      py={1.5}
      borderRadius="full"
      border="1px solid"
      borderColor="whiteAlpha.200"
      bg="whiteAlpha.50"
    >
      <Box w="7px" h="7px" borderRadius="full" bg={ACCENT} flexShrink={0} />
      <Text
        textStyle="badge"
        color="fg.emphasized"
        letterSpacing="0.08em"
        textTransform="uppercase"
      >
        Open to collaboration
      </Text>
    </Flex>

    <Heading
      as="h1"
      fontWeight="900"
      fontSize={{ base: '4xl', md: '6xl', xl: '7xl' }}
      lineHeight="0.95"
      letterSpacing="-0.03em"
      textTransform="uppercase"
    >
      <Text as="span" display="block">
        Let&apos;s build
      </Text>
      <Text
        as="span"
        display="block"
        style={{
          background: `linear-gradient(100deg, ${ACCENT} 0%, #8E7BFF 55%, ${ACCENT} 100%)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}
      >
        something together
      </Text>
    </Heading>

    <Text textStyle={{ base: 'md', md: 'lg' }} lineHeight="1.7" color="fg.muted" maxW="640px">
      Teamplay is the easiest way for gamers to find people to play with. We are open to
      partnerships, sponsorships, and investment – if you want to grow the social play space with
      us, let&apos;s talk.
    </Text>

    <Flex gap={3} flexDir={{ base: 'column', sm: 'row' }} w={{ base: '100%', sm: 'auto' }} mt={1}>
      <ExternalButton
        href={MAILTO}
        action="hero_contact"
        size="lg"
        minW="200px"
        w={{ base: '100%', sm: 'auto' }}
        style={{ boxShadow: `0 0 26px ${ACCENT}55` }}
      >
        Get in touch
      </ExternalButton>
      <Button
        asChild
        size="lg"
        variant="ghost"
        color="fg.muted"
        w={{ base: '100%', sm: 'auto' }}
        _hover={{ bg: 'whiteAlpha.50', color: 'fg.emphasized' }}
      >
        <a href="#tracks">See how we can work together</a>
      </Button>
    </Flex>
  </Stack>
);

const TrackCard = ({ item }: { item: CollabTrack }) => (
  <Flex
    direction="column"
    h="100%"
    p={{ base: 5, md: 6 }}
    gap={4}
    borderRadius="2xl"
    border="1px solid"
    borderColor="whiteAlpha.100"
    background="linear-gradient(165deg, hsla(228, 11.6%, 14%, 0.85) 0%, hsla(228, 11.6%, 9%, 0.6) 100%)"
    transition="border-color 0.2s, transform 0.2s"
    _hover={{ borderColor: 'whiteAlpha.300', transform: 'translateY(-2px)' }}
  >
    <Text textStyle="micro" color="purple.300" textTransform="uppercase" letterSpacing="0.1em">
      {item.eyebrow}
    </Text>
    <Heading as="h3" fontSize={{ base: 'lg', md: 'xl' }} fontWeight="800" lineHeight="1.25">
      {item.title}
    </Heading>
    <Text textStyle="sm" color="fg.muted" lineHeight="1.6">
      {item.summary}
    </Text>

    <Stack as="ul" gap={1.5} listStyleType="none" m={0} p={0}>
      {item.points.map((point) => (
        <Flex as="li" key={point} gap={2} alignItems="flex-start">
          <Box mt="7px" w="5px" h="5px" borderRadius="full" bg={ACCENT} flexShrink={0} />
          <Text textStyle="sm" color="fg.muted" lineHeight="1.5">
            {point}
          </Text>
        </Flex>
      ))}
    </Stack>

    <Box mt="auto" pt={1}>
      <ExternalButton href={MAILTO} action={item.ctaAction} size="md" w="100%">
        {item.cta}
      </ExternalButton>
    </Box>
  </Flex>
);

const CollabTracks = () => (
  <Box id="tracks" css={{ scrollMarginTop: '80px' }}>
    <SectionHeader
      eyebrow="Ways to work together"
      title="Pick what fits"
      subtitle="Partnerships, sponsorships, investment – or something we haven't thought of yet. Reach out and we'll shape it."
    />
    <SimpleGrid columns={{ base: 1, lg: 3 }} gap={{ base: 4, md: 6 }} alignItems="stretch">
      {COLLAB_TRACKS.map((item) => (
        <TrackCard key={item.id} item={item} />
      ))}
    </SimpleGrid>
  </Box>
);

const WhyPartner = () => (
  <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={{ base: 4, md: 5 }}>
    {VALUE_POINTS.map((point) => (
      <Box
        key={point.title}
        p={6}
        borderRadius="2xl"
        border="1px solid"
        borderColor="whiteAlpha.100"
        background="linear-gradient(165deg, hsla(228, 11.6%, 15%, 0.7) 0%, hsla(228, 11.6%, 9%, 0.4) 100%)"
      >
        <Heading as="h3" fontSize="md" fontWeight="800" mb={2}>
          {point.title}
        </Heading>
        <Text textStyle="sm" color="fg.muted" lineHeight="1.6">
          {point.description}
        </Text>
      </Box>
    ))}
  </SimpleGrid>
);

const HowItWorks = () => (
  <Box>
    <SectionHeader
      eyebrow="How it works"
      title="Getting started is simple"
      subtitle="No long process. A hello, a quick chat, and we make it happen."
    />
    <Grid
      templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
      gap={{ base: 4, md: 5 }}
      maxW="900px"
      mx="auto"
    >
      {PROCESS_STEPS.map((item) => (
        <Box
          key={item.step}
          p={6}
          borderRadius="2xl"
          border="1px solid"
          borderColor="whiteAlpha.100"
          background="linear-gradient(165deg, hsla(228, 11.6%, 15%, 0.7) 0%, hsla(228, 11.6%, 9%, 0.4) 100%)"
        >
          <Flex
            w="34px"
            h="34px"
            borderRadius="full"
            alignItems="center"
            justifyContent="center"
            fontWeight="800"
            fontSize="md"
            color="white"
            mb={4}
            style={{ background: `linear-gradient(135deg, ${ACCENT} 0%, #8E7BFF 100%)` }}
          >
            {item.step}
          </Flex>
          <Heading as="h3" fontSize="md" fontWeight="800" mb={2}>
            {item.title}
          </Heading>
          <Text textStyle="sm" color="fg.muted" lineHeight="1.6">
            {item.description}
          </Text>
        </Box>
      ))}
    </Grid>
  </Box>
);

const FinalCta = () => (
  <Box position="relative" w="100%">
    <Box
      position="absolute"
      top="-70px"
      left="50%"
      transform="translateX(-50%)"
      w="360px"
      h="360px"
      background={`radial-gradient(circle, ${ACCENT}26 0%, transparent 70%)`}
      pointerEvents="none"
    />
    <Flex
      direction="column"
      alignItems="center"
      gap={5}
      py={{ base: 12, md: 16 }}
      px={{ base: 6, md: 12 }}
      background="linear-gradient(165deg, hsla(228, 11.6%, 15%, 0.85) 0%, hsla(228, 11.6%, 9%, 0.5) 100%)"
      border="1px solid"
      borderColor="whiteAlpha.100"
      borderRadius="3xl"
      textAlign="center"
      position="relative"
      overflow="hidden"
    >
      <Box
        position="absolute"
        top={0}
        left="10%"
        right="10%"
        h="1px"
        background={`linear-gradient(90deg, transparent, ${ACCENT}88, transparent)`}
      />
      <Heading
        as="h2"
        fontSize={{ base: '3xl', md: '5xl' }}
        lineHeight="1.02"
        maxW="620px"
        fontWeight="900"
        textTransform="uppercase"
        letterSpacing="-0.02em"
      >
        Let&apos;s talk
      </Heading>
      <Text textStyle="md" color="fg.muted" maxW="600px">
        Partnership, sponsorship, or investment – tell us what you have in mind and we&apos;ll take
        it from there.
      </Text>
      <Flex gap={3} flexDir={{ base: 'column', sm: 'row' }} w={{ base: '100%', sm: 'auto' }} mt={2}>
        <ExternalButton
          href={MAILTO}
          action="footer_contact"
          size="lg"
          minW="220px"
          w={{ base: '100%', sm: 'auto' }}
          style={{ boxShadow: `0 0 28px ${ACCENT}59` }}
        >
          Get in touch
        </ExternalButton>
        <ExternalButton
          href={DISCORD_URL}
          action="footer_discord"
          size="lg"
          variant="outline"
          color="fg.emphasized"
          borderColor="whiteAlpha.200"
          w={{ base: '100%', sm: 'auto' }}
          _hover={{ borderColor: 'whiteAlpha.400', bg: 'whiteAlpha.50' }}
        >
          Join our Discord
        </ExternalButton>
      </Flex>
      <Text textStyle="sm" color="fg.subtle" mt={1}>
        Or email us directly at{' '}
        <Text asChild color="purple.300" fontWeight="600">
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </Text>
      </Text>
    </Flex>
  </Box>
);

export const PartnerWithUs = () => (
  <Stack
    position="relative"
    m="0 auto"
    gap={0}
    w="100%"
    maxW={{ base: '640px', lg: '1200px' }}
    px={{ base: 4, md: 6, lg: 8 }}
    mb={4}
    // Landing-scoped contrast + accent overrides (same trick as Landing / careers):
    // brighten muted/subtle foreground for AA on dark, and tune purple accent tokens.
    css={{
      '--chakra-colors-fg-muted': 'rgba(255, 255, 255, 0.55)',
      '--chakra-colors-fg-subtle': 'rgba(255, 255, 255, 0.5)',
      '--chakra-colors-purple-300': '#8288f5',
      '--chakra-colors-purple-200': '#8288f5',
      '--chakra-colors-purple-fg': '#8288f5',
      '--chakra-colors-purple-solid': '#5158e2',
    }}
  >
    {/* Hero — full-bleed animated grid-glow background, content stays in the 1200px grid. */}
    <Box
      position="relative"
      w="100vw"
      ml="calc(50% - 50vw)"
      mr="calc(50% - 50vw)"
      mt={{ base: -5, lg: -5 }}
    >
      <GridGlowBackground>
        <Box
          maxW={{ base: '640px', lg: '1200px' }}
          mx="auto"
          px={{ base: 4, md: 6, lg: 8 }}
          py={{ base: 12, lg: 20 }}
        >
          <Hero />
        </Box>
      </GridGlowBackground>
    </Box>

    {/* Ways to work together — base */}
    <Box py={{ base: 12, md: 16 }}>
      <CollabTracks />
    </Box>

    {/* Why partner — elevated colour band */}
    <SectionBand background={BAND_ELEVATED}>
      <Box py={{ base: 12, md: 16 }}>
        <SectionHeader
          eyebrow="Why Teamplay"
          title="A partner worth backing"
          subtitle="An engaged gaming audience, a product native to how people play, and a team that moves fast."
        />
        <WhyPartner />
      </Box>
    </SectionBand>

    {/* How it works — base */}
    <Box py={{ base: 12, md: 16 }}>
      <HowItWorks />
    </Box>

    {/* Final CTA — base */}
    <Box py={{ base: 12, md: 16 }}>
      <FinalCta />
    </Box>
  </Stack>
);
